import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// This is a simplified version of the MapComponent that would be used with the IrrigationAssetsMap

const MapComponent = ({
  provider,
  drawings,
  initialCenter,
  initialZoom,
  onMapReady,
  onMapViewChange,
  onDrawCreated,
  onDrawEdited,
  onDrawDeleted,
  onDrawStart,
  onDrawStop,
  drawingMode,
  isDrawingToolActive
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawControlRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const freeDrawRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize the map on first render
  useEffect(() => {
    // Skip if already initialized
    if (isInitializedRef.current || !window.L) return;

    const L = window.L;
    
    // Create map instance
    const map = L.map(mapContainerRef.current, {
      center: initialCenter || [33.55, -112.0],
      zoom: initialZoom || 18,
      zoomControl: true
    });
    
    // Create layer for drawn items
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    
    // Initialize draw control
    const drawControl = new L.Control.Draw({
      edit: {
        featureGroup: drawnItems,
        poly: {
          allowIntersection: false
        }
      },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true
        },
        marker: true,
        circle: true,
        circlemarker: false,
        rectangle: false,
        polyline: true
      }
    });
    map.addControl(drawControl);
    
    // Add tile layer based on provider
    L.tileLayer(provider.url, {
      attribution: provider.attribution,
      maxZoom: provider.maxZoom,
      subdomains: provider.subdomains
    }).addTo(map);
    
    // Set up event listeners
    map.on('moveend', () => {
      if (onMapViewChange) {
        onMapViewChange({
          center: map.getCenter(),
          zoom: map.getZoom(),
          bounds: map.getBounds()
        });
      }
    });
    
    map.on('draw:created', (e) => {
      drawnItems.addLayer(e.layer);
      if (onDrawCreated) onDrawCreated(e);
    });
    
    map.on('draw:edited', (e) => {
      if (onDrawEdited) onDrawEdited(e);
    });
    
    map.on('draw:deleted', (e) => {
      if (onDrawDeleted) onDrawDeleted(e);
    });
    
    map.on('draw:drawstart', (e) => {
      if (onDrawStart) onDrawStart(e);
    });
    
    map.on('draw:drawstop', (e) => {
      if (onDrawStop) onDrawStop(e);
    });
    
    // Store references
    mapRef.current = map;
    drawControlRef.current = drawControl;
    drawnItemsRef.current = drawnItems;
    
    // Call onMapReady callback
    if (onMapReady) {
      onMapReady({
        map,
        drawnItemsLayer: drawnItems,
        drawControlInstance: drawControl
      });
    }
    
    isInitializedRef.current = true;
    
    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, []);
  
  // Update tile layer when provider changes
  useEffect(() => {
    if (!mapRef.current || !provider) return;
    
    const map = mapRef.current;
    
    // Remove existing tile layers
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });
    
    // Add new tile layer
    L.tileLayer(provider.url, {
      attribution: provider.attribution,
      maxZoom: provider.maxZoom,
      subdomains: provider.subdomains
    }).addTo(map);
  }, [provider]);
  
  // Render drawings on the map
  useEffect(() => {
    if (!mapRef.current || !drawnItemsRef.current) return;
    if (onMapReady) {
      onMapReady({
        map: mapRef.current,
        drawnItemsLayer: drawnItemsRef.current,
        drawControlInstance: drawControlRef.current
      });
    }
  }, []);
  
  return (
    <div 
      ref={mapContainerRef} 
      style={{ width: '100%', height: '100%', minHeight: '600px' }}
    />
  );
};

MapComponent.propTypes = {
  provider: PropTypes.object.isRequired,
  drawings: PropTypes.array,
  initialCenter: PropTypes.array,
  initialZoom: PropTypes.number,
  onMapReady: PropTypes.func,
  onMapViewChange: PropTypes.func,
  onDrawCreated: PropTypes.func,
  onDrawEdited: PropTypes.func,
  onDrawDeleted: PropTypes.func,
  onDrawStart: PropTypes.func,
  onDrawStop: PropTypes.func,
  drawingMode: PropTypes.string,
  isDrawingToolActive: PropTypes.bool
};

MapComponent.defaultProps = {
  drawings: [],
  initialCenter: [33.55, -112.0],
  initialZoom: 18,
  drawingMode: 'polygon',
  isDrawingToolActive: false
};

export default MapComponent;
